import { useState, useEffect } from "react";
import RightTimeApi from "@/API";
import DateTimePicker from "../DateTimePicker";
import Modal from "@modalSrc/Modal";
import ChangeCompanyModal from "./ChangeCompanyModal";

type TestEditOptionValue = string | number | null | undefined;
type TestEditOptions = Record<string, TestEditOptionValue>;

export default function TestEditModal(props) {
  const {
    isOpen,
    onDownload,
    onDownloadV40,
    onDownloadInfo,
    checkedItems,
    onClose,
    downloadInfo,
    statuses,
    sites,
    updateSpinner = () => { },
  } = props;
  const [options, updateOptions] = useState<TestEditOptions>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showChangeCompanyModal, setShowChangeCompanyModal] = useState(false);

  if (!isOpen) {
    return null;
  }

  function onUpdateOptions(e) {
    const t = typeof e;
    const field = e.target.getAttribute("data-field");
    const type = e.target.getAttribute("data-type");
    updateOptions((prevOptions) => ({
      ...prevOptions,
      [field]: e.target.value,
    }));
  }

  function toggleCheck(e) {
    const field = e.target.getAttribute("data-field");
    //console.log('toggleCheck ', field, ' to', (!options[field] ? "true" : "false"));
    toggleField(field);
  }

  function toggleField(field: string) {
    if (!field) return;
    updateOptions((prevOptions) => {
      const newOptions = { ...prevOptions };
      if (newOptions.hasOwnProperty(field)) {
        delete newOptions[field];
      } else {
        newOptions[field] = '';
      }
      return newOptions;
    });
  }

  function renderDateOption(field: string, label: string, className = "") {
    const id = `date-toggle-${field}`;
    const hasValue = String(options[field] || "").trim().length > 0;

    return (
      <li className={className}>
        <label htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            data-field={field}
            checked={isChecked(field)}
            onChange={toggleCheck}
          />
          {label}
        </label>
        {isChecked(field) && (
          <span
            className="dateOptionPicker"
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
          >
            <DateTimePicker
              field={field}
              pickerTitle={label}
              value={options[field]}
              onChange={onUpdateOptions}
              openOnMount={!options[field]}
              displayAsText={!hasValue}
              className={field === "CodeExpiration" ? "CodeExpirationPicker" : undefined}
            />
          </span>
        )}
      </li>
    );
  }

  function isChecked(param) {
    if (typeof param === 'string') {
      // console.log('isChecked called with string:', param);
      return options.hasOwnProperty(param); // Ensure the return value is a boolean
    } else if (param && param.target) {
      // console.log('isChecked called with event:', param);
      return !!options[param.target.getAttribute("data-field")]; // Ensure the return value is a boolean
    } else {
      console.log('isChecked called with unknown param:', param);
      return false;
    }
  }

  function handleChangeCompanyConfirm(companyId: number, siteId: number) {
    updateOptions((prevOptions) => ({
      ...prevOptions,
      CompanyId: companyId,
      CompanySiteId: siteId,
    }));
  }

  async function handleApplyAsync() {
    setIsLoading(true);
    updateSpinner(true);

    const selectedOptions = { ...options };
    const hasDownload = Object.prototype.hasOwnProperty.call(selectedOptions, "Download");
    const hasDownloadV40 = Object.prototype.hasOwnProperty.call(selectedOptions, "DownloadV40");
    const hasDownloadInfo = Object.prototype.hasOwnProperty.call(selectedOptions, "DownloadInfo");

    delete selectedOptions.Download;
    delete selectedOptions.DownloadV40;
    delete selectedOptions.DownloadInfo;

    try {
      // Downloads are fire-and-forget (new window), so treat initiation as completion.
      if (hasDownload && onDownload) {
        await Promise.resolve(onDownload(checkedItems));
      }
      if (hasDownloadV40 && onDownloadV40) {
        await Promise.resolve(onDownloadV40(checkedItems));
      }
      if (hasDownloadInfo && onDownloadInfo) {
        await Promise.resolve(onDownloadInfo(checkedItems));
      }

      const hasApiChanges = Object.keys(selectedOptions).length > 0;
      if (hasApiChanges) {
        const changesArray = Object.keys(selectedOptions).map((key) => ({
          name: key,
          value: selectedOptions[key],
        }));

        const data = {
          testIds: checkedItems,
          changes: changesArray,
        };

        const resp = await RightTimeApi.putAsync("/Tests", data);
        if (!(resp && resp.ok)) {
          console.error("Error in postFile:", resp?.statusText);
          return;
        }
      }

      if (hasDownload || hasDownloadV40 || hasDownloadInfo || hasApiChanges) {
        CloseModal();
      }
    } catch (error) {
      console.error("Error in postFile:", error);
    } finally {
      updateSpinner(false);
      setIsLoading(false);
    }
  }

  function CloseModal() {
    updateOptions({});
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={CloseModal}
      title="Test Modification"
      className="testEditModal"
      size="md"
    >
      {isLoading ? (
        <div className="loading-overlay">
          <div className="spinner" />
        </div>
      ) : null}
      <span>
        Select the actions you wish to take on the{" "}
        {checkedItems ? checkedItems.length : "?"} selected test(s)
      </span>
      <ul className="testEditOptions">
        {onDownload && (
          <li>
            <label>
              <input
                type="checkbox"
                name="testModification"
                value="Download"
                checked={isChecked("Download")}
                onChange={toggleCheck}
                data-field="Download"
              />
              Download Data V20
            </label>
          </li>)}
        {onDownloadV40 && (
          <li>
            <label>
              <input
                type="checkbox"
                name="testModification"
                value="DownloadV40"
                checked={isChecked("DownloadV40")}
                onChange={toggleCheck}
                data-field="DownloadV40"
              />
              Download Data V40
            </label>
          </li>)}
        {downloadInfo ? (
          <li className="DownloadInfoOption">
            <label>
              <input
                type="checkbox"
                value="DownloadInfo"
                checked={isChecked("DownloadInfo")}
                onChange={toggleCheck}
                data-field="DownloadInfo"
                className="DownloadInfoCheckbox"
              />
              Download Info
            </label>
          </li>) : null}

        <li className="SetStatusOption">
          <label>
            <input type="checkbox" value="Status" checked={isChecked("Status")} onChange={toggleCheck} data-field="Status" />
            Set Status
          </label>
          {isChecked("Status") && (
            <select value={options["Status"]} onChange={onUpdateOptions} data-field="Status" data-type="String">
              <option key="0" value="0">Select Status</option>
              <option key="-1" value="none">none</option>
              {statuses.map((status) => (
                <option key={status.ctsId} value={status.ctsId}>
                  {status.description}
                </option>
              ))}
            </select>
          )}
        </li>
        {checkedItems.length === 1 && (<>
          <li className="SetFirstNameOption">
            <label>
              <input type="checkbox" value="FirstName" checked={isChecked("FirstName")} onChange={toggleCheck} data-field="FirstName" />
              First Name
            </label>
              {isChecked("FirstName") && (
                <input value={options["FirstName"]} onChange={onUpdateOptions} data-field="FirstName" data-type="String" />
              )}
          </li>
          <li className="SetLastNameOption">
            <label>
              <input type="checkbox" value="LastName" checked={isChecked("LastName")} onChange={toggleCheck} data-field="LastName" />
              Last Name
            </label>
              {isChecked("LastName") && (
                <input value={options["LastName"]} onChange={onUpdateOptions} data-field="LastName" data-type="String" />
              )}
          </li>
        </>
        )}
        <li className="SetJobOption">
          <label>
            <input type="checkbox" value="Job" checked={isChecked("Job")} onChange={toggleCheck} data-field="Job" />
            Job
          </label>
          {isChecked("Job") && (
            <input value={options["Job"]} onChange={onUpdateOptions} data-field="Job" data-type="String" />
          )}
        </li>
        {renderDateOption("CodeExpiration", "Code Expiration", "SetCodeExpirationOption")}
        {renderDateOption("CreateTime", "Create Time", "SetCreateTimeOption")}
        {renderDateOption("StartTime", "Start Time")}
        {renderDateOption("FinishTime", "Finish Time", "SetFinishTimeOption")}
        {renderDateOption("PulledTime", "Pulled Time", "SetPulledTimeOption")}
        <li className="SetResetScoresOption">
          <label>
            <input
              type="checkbox"
              data-field="ResetScores"
              checked={isChecked("ResetScores")}
              onChange={toggleCheck}
            />
            Reset Scoring
          </label>
        </li>
        {renderDateOption("EmbargoTime", "Embargo Time", "SetEmbargoTimeOption")}
        <li className="SetCheckRetestOption">
          <label>
            <input
              type="checkbox"
              data-field="CheckRetest"
              checked={isChecked("CheckRetest")}
              onChange={toggleCheck}
            />
            Check for Retest
          </label>
        </li>
        <li className="SetChangeSiteOption">
          <label>
            <input
              type="checkbox"
              data-field="SiteId"
              checked={isChecked("SiteId")}
              onChange={toggleCheck}
            />
            Change Site
            {isChecked("SiteId") && (
              <select
                value={options["SiteId"]}
                onChange={onUpdateOptions}
                data-type="int"
                data-field="SiteId"
              >
                <option key="0" value="0">
                  Select Site
                </option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            )}
          </label>
        </li>
        <li className="SetChangeCompanyOption">
          <label>
            <input
              type="checkbox"
              data-field="CompanyId"
              checked={isChecked("CompanyId")}
              onChange={toggleCheck}
            />
            Change Company
            {isChecked("CompanyId") && (
              <button
                type="button"
                className="selectChangeCompanyButton"
                onClick={() => setShowChangeCompanyModal(true)}
              >
                {options["CompanyId"] && options["CompanySiteId"]
                  ? "Change..."
                  : "Select Company & Site"}
              </button>
            )}
          </label>
        </li>
        <li>
          <label>
            <input
              type="checkbox"
              data-field="Delete"
              checked={isChecked("Delete")}
              onChange={toggleCheck}
            />
            Delete / Archive
          </label>
        </li>
        <li className="SetResetOption">
          <label>
            <input
              type="checkbox"
              data-field="Reset"
              checked={isChecked("Reset")}
              onChange={toggleCheck}
            />
            Reset (return to post-creation state)
          </label>
        </li>
      </ul>
      <div className="actions">
        <button className="okButton" onClick={handleApplyAsync}>Apply</button>
      </div>

      <ChangeCompanyModal
        isOpen={showChangeCompanyModal}
        onClose={() => setShowChangeCompanyModal(false)}
        onConfirm={handleChangeCompanyConfirm}
      />
    </Modal>
  );
}

